// WHAT: Component for displaying a module section with status columns in Kanban board
// WHY:  Groups roadmap items by module and displays them in status-based columns
// DOMAIN: Admin → Roadmap → Kanban
// DEPENDENCIES: Stack, Heading, Badge components, KanbanColumn component
// RISKS:
//   - NOTE: Horizontal scroll may occur if many columns - consider responsive design
//   - NOTE: Module data structure assumes specific status keys (open, in_progress, done, rejected)

import { LucideFolderOpen } from "lucide-react";
import React from "react";

import { Heading, Badge } from "@/components/ui";

import { type Roadmap_Item } from "../../../types/roadmap-types";
import { KanbanColumn } from "./KanbanColumn";

/**
 * Props for KanbanModule component
 */
interface KanbanModuleProperties {
  /** Object mapping status keys to arrays of roadmap items */
  moduleData: Record<string, Roadmap_Item[]>;
  /** Name of the module */
  moduleName: string;
  /** Callback when add button is clicked */
  onAddItem: (module: string) => void;
  /** Callback when item is clicked */
  onItemClick: (item: Roadmap_Item) => void;
}

/**
 * WHAT:
 *   Component that displays a module section with status-based columns
 *   for organizing roadmap items in a Kanban board layout.
 *
 * WHY:
 *   Groups items by module and displays them in status columns, providing
 *   a clear view of module-specific workflow status.
 *
 * PROPS:
 *   - moduleName: string — Name of the module
 *   - moduleData: Record<string, Roadmap_Item[]> — Status-to-items mapping
 *   - onItemClick: (item: Roadmap_Item) => void — Handler for item clicks
 *   - onAddItem: (module: string) => void — Handler for add button clicks
 *
 * BEHAVIOR:
 *   - Displays module header with name and total item count
 *   - Renders four status columns (Open, In Progress, Done, Rejected)
 *   - Calculates total items across all statuses
 *   - Uses horizontal scroll for columns
 *
 * RISKS:
 *   - NOTE: Assumes specific status keys in moduleData - missing keys default to empty array
 *   - NOTE: Horizontal scroll may cause UX issues on mobile devices
 *   - NOTE: Total items calculation includes all statuses - may not match filtered data
 *
 * @param root0
 *
 * @param root0.moduleName
 *
 * @param root0.moduleData
 *
 * @param root0.onItemClick
 *
 * @param root0.onAddItem
 *
 * @returns {JSX.Element} Die gerenderte KanbanModule-Komponente
 *
 */
export const KanbanModule: React.FC<KanbanModuleProperties> = ({
  moduleData,
  moduleName,
  onAddItem,
  onItemClick,
}) => {
  const statusColumns = [
    { items: moduleData.open ?? [], key: "open", title: "Open" },
    {
      items: moduleData.in_progress ?? [],
      key: "in_progress",
      title: "In Progress",
    },
    { items: moduleData.done ?? [], key: "done", title: "Done" },
    { items: moduleData.rejected ?? [], key: "rejected", title: "Rejected" },
  ];

  const totalItems = statusColumns.reduce((sum, col) => sum + col.items.length, 0);

  return (
    <div className="space-y-4">
      {/* Module Header */}
      <div className="flex items-center gap-3">
        <LucideFolderOpen className="w-5 h-5 text-tracq-bright-blue" />
        <Heading className="text-lg font-semibold text-white" level={3}>
          {moduleName}
        </Heading>
        <Badge size="sm" style="subtle" variant="default">
          {totalItems} items
        </Badge>
      </div>

      {/* Module Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {statusColumns.map((column) => (
          <KanbanColumn
            items={column.items}
            key={column.key}
            module={moduleName}
            onAddItem={onAddItem}
            onItemClick={onItemClick}
            status={column.key}
            title={column.title}
          />
        ))}
      </div>
    </div>
  );
};
